import {customElement, property, query, state} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {UnLitElement, WithEopSlots} from "../../../common/elements";
import {type ContactsResponse, ContactsService} from "../contactsService";
import {resolve} from "../../../container";
import {ContactsRequest} from "../requests";
import {EopOverlaySpinner, OverlaySpinnerPosition} from "../../../page/elements/spinner";
import {LanguagesService} from "../../../common/languages";
import {SearchFacade} from "./searchFacade";
import {schedule} from "../../../common/utils/promises";
import type {Nullable} from "../../../bootstrap/common/types";
import {EopErrorHandler} from "../../../common/errorHandler";
import "../../../page/elements/businessCard";
import {type BusinessCardData} from "../../../page/elements/businessCard";

@customElement("eop-news-search-contacts")
export class EopNewsSearchContacts extends WithEopSlots(UnLitElement) {

    @property({attribute: "contact-mode"})
    private contactMode: string;
    @property({attribute: "default-contact-slot-name"})
    private defaultContactSlotName: string;

    @state()
    private contacts: BusinessCardData[] = [];

    @query("eop-overlay-spinner")
    private spinner: EopOverlaySpinner;

    public constructor(
        private contactsService: ContactsService = resolve(ContactsService),
        private languages: LanguagesService = resolve(LanguagesService),
        private searchFacade: SearchFacade = resolve(SearchFacade),
        private errorHandler: EopErrorHandler = resolve(EopErrorHandler)
    ) {
        super();
    }

    public render(): Nullable<TemplateResult> {
        return html`
            <eop-overlay-spinner .position=${OverlaySpinnerPosition.TOP}></eop-overlay-spinner>
            ${this.renderContactsElement(this.contacts)}
        `;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        if (this.contactMode === "no_contact") {
            return;
        }
        this.searchFacade.onNewSearchPhrase(searchPhrase => {
            const request = new ContactsRequest(this.languages.activeLanguageId(), searchPhrase);
            const contactsPromise = this.contactsService.contacts(request)
                .then(response => this.contacts = this.mapContacts(response))
                .catch(e => this.errorHandler.report(e));
            void schedule(this.spinner.spinWhile(contactsPromise)).as("contacts");
        });
    }

    private renderContactsElement(contacts: BusinessCardData[]): TemplateResult {
        if (contacts.isEmpty()) {
            return html`${this.resolveSlot(this.defaultContactSlotName)}`;
        } else {
            return html`${(this.renderBusinessCards(contacts))}`;
        }
    }

    private renderBusinessCards(contacts: BusinessCardData[]): TemplateResult[] {
        return contacts.map(contact => html`<eop-business-card .data=${contact} class="reverse-background"></eop-business-card>`);
    }

    private mapContacts(response: ContactsResponse): BusinessCardData[] {
        return response.contacts.map(contact => ({
            title: contact.title,
            imageUrl: contact.imageUrl,
            imageAlt: contact.imageAltText,
            email: contact.contactInfo.email,
            phone: contact.contactInfo.phone,
            fax: contact.contactInfo.fax,
            mobile: contact.contactInfo.mobile,
            infoBlock: contact.infoBlock,
            location: contact.address.city,
            address: contact.address.street,
            link: contact.link,
            corporation: contact.corporationName
        }));
    }
}